import React from "react";

export type MonthlyBar = { month: string; count: number };

export default function MonthlyBarsCard({
  title,
  subtitle,
  data,
}: {
  title: string;
  subtitle?: string;
  data: MonthlyBar[];
}) {
  const H = 200;
  const chartTop = 18;
  const chartBottom = 24;
  const chartHeight = H - chartTop - chartBottom;

  const barW = 18;
  const gap = 18;
  const W = data.length * (barW + gap) + gap;

  const counts = data.map((d) => Math.max(0, d.count || 0));
  const maxCount = counts.length ? Math.max(...counts) : 0;
  const safeMax = maxCount || 1; // avoid division by 0

  const numTicks = 4;
  const step = Math.max(1, Math.ceil(safeMax / numTicks));
  const maxTick = step * numTicks;
  const gridTicks = Array.from({ length: numTicks + 1 }, (_, i) => i * step);

  const yForValue = (val: number) =>
    chartTop + (1 - val / maxTick) * chartHeight;

  return (
    // <div className="rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
    <div className="rounded-2xl bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div className="p-5 md:p-6">
        <div className="text-[15px] font-semibold text-[#111]">{title}</div>
        {subtitle && (
          <div className="text-[12px] text-[#6B7280] mb-2 mt-1">{subtitle}</div>
        )}

        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[210px]">
          {gridTicks.map((t) => (
            <g key={t}>
              <line
                x1={0}
                y1={yForValue(t)}
                x2={W}
                y2={yForValue(t)}
                stroke="#E5E7EB"
                strokeWidth={t === 0 ? 2 : 1}
              />
              <text x={0} y={yForValue(t) - 4} fontSize="10" fill="#9CA3AF">
                {t}
              </text>
            </g>
          ))}

          {/* bars */}
          {data.map((d, i) => {
            const v = Math.max(0, d.count || 0);
            const x = gap + i * (barW + gap);
            const y = yForValue(v);
            const height = chartTop + chartHeight - y;

            const isCurrent =
              new Date()
                .toLocaleString("default", { month: "short" })
                .toLowerCase() === d.month.slice(0, 3).toLowerCase();
            const fill = isCurrent ? "#111111" : "#BFE6BF";

            return (
              <g key={i}>
                <rect x={x} y={y} width={barW} height={height} rx="6" fill={fill} />
                <text
                  x={x + barW / 2}
                  y={H - 6}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#777B7F"
                >
                  {d.month.slice(0, 3)}
                </text>
                {/* optional value label above bar */}
                {v > 0 && (
                  <text
                    x={x + barW / 2}
                    y={y - 4}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#4B5563"
                  >
                    {v}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
